/**
 * Product Delivery Component
 * 
 * Displays delivery and post-delivery metrics with a vertical bar chart:
 * - Invoiced → Delivered → Installed
 * - Active customers
 * - Support tickets
 * - Returns
 */

import React from 'react';
import { Tag, Tooltip, Row, Col } from 'antd';
import {
  SendOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  RollbackOutlined,
  DollarOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { ProcessStepsChart, ProcessStep } from './ProcessStepsChart';
import { fetchProductDomainDashboardComponent } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import styles from './ProductDeliveryComponent.module.css';

interface ProductDeliveryComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Whether to use compact view */
  compact?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const getDeliverySteps = (data: Record<string, any>): ProcessStep[] => {
  if (Array.isArray(data.delivery_steps) && data.delivery_steps.length > 0) {
    return data.delivery_steps.map((s: { id: string; name: string; count: number; color?: string }) => ({
      id: s.id,
      name: s.name,
      count: s.count ?? 0,
      color: s.color ?? '#8b5cf6',
    }));
  }
  return [
    {
      id: 'invoiced',
      name: 'Invoiced',
      count: data.product_invoice ?? data.invoiced ?? 0,
      color: '#a78bfa'
    },
    {
      id: 'delivered',
      name: 'Delivered',
      count: data.products_delivered ?? data.delivered ?? 0,
      color: '#8b5cf6'
    },
    {
      id: 'installed',
      name: 'Installed',
      count: data.products_installed ?? data.installed ?? 0,
      color: '#7c3aed'
    }
  ];
};

export const ProductDeliveryComponent: React.FC<ProductDeliveryComponentProps> = ({
  compact = false,
  ...props
}) => {
  const renderContent = (data: ComponentData, loading: boolean) => {
    const steps = getDeliverySteps(data.data);
    
    const paymentReceived = data.data.payment_received ?? 0;
    const activeCustomers = data.data.active_customers ?? 0;
    const supportTickets = data.data.product_support_tickets ?? 0;
    const returns = data.data.product_returns ?? 0;
    
    // Calculate return rate
    const delivered = data.data.products_delivered ?? data.data.delivered ?? 0;
    const returnRate = delivered > 0 
      ? ((returns / delivered) * 100).toFixed(1) 
      : '0';

    return (
      <div className={`${styles.productDelivery} ${compact ? styles.compact : ''}`}>
        {!compact && (
          <div className={styles.header}>
            <h4 className={styles.title}>
              <SendOutlined /> Delivery & Support
            </h4>
          </div>
        )}

        {/* Bar Chart */}
        <div className={styles.chartWrapper}>
          <ProcessStepsChart
            steps={steps}
            height={compact ? 140 : 180}
            barColor="#8b5cf6"
            showLabels={true}
            showConversionRates={!compact}
            compact={compact}
          />
        </div>

        {/* Customer & Support Metrics */}
        <div className={styles.metricsSection}>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={compact ? 12 : 6}>
              <Tooltip title="Payment Received">
                <div className={styles.metricCard}>
                  <DollarOutlined className={styles.metricIcon} style={{ color: '#10b981' }} />
                  <div className={styles.metricContent}>
                    <span className={styles.metricValue} style={{ color: '#10b981' }}>
                      {formatCurrency(paymentReceived)}
                    </span>
                    <span className={styles.metricLabel}>Revenue</span>
                  </div>
                </div>
              </Tooltip>
            </Col>

            <Col xs={12} sm={compact ? 12 : 6}>
              <Tooltip title="Active Customers">
                <div className={styles.metricCard}>
                  <TeamOutlined className={styles.metricIcon} style={{ color: '#3b82f6' }} />
                  <div className={styles.metricContent}>
                    <span className={styles.metricValue}>{activeCustomers}</span>
                    <span className={styles.metricLabel}>Customers</span>
                  </div>
                </div>
              </Tooltip>
            </Col>

            <Col xs={12} sm={compact ? 12 : 6}>
              <Tooltip title="Open Support Tickets">
                <div className={`${styles.metricCard} ${supportTickets > 10 ? styles.alert : ''}`}>
                  <CustomerServiceOutlined 
                    className={styles.metricIcon} 
                    style={{ color: supportTickets > 10 ? '#ef4444' : '#f59e0b' }} 
                  />
                  <div className={styles.metricContent}>
                    <span className={styles.metricValue}>
                      {supportTickets}
                      {supportTickets > 10 && (
                        <ExclamationCircleOutlined className={styles.alertIcon} />
                      )}
                    </span>
                    <span className={styles.metricLabel}>Tickets</span>
                  </div>
                </div>
              </Tooltip>
            </Col>

            <Col xs={12} sm={compact ? 12 : 6}>
              <Tooltip title={`Return Rate: ${returnRate}%`}>
                <div className={`${styles.metricCard} ${parseFloat(returnRate) > 5 ? styles.alert : ''}`}>
                  <RollbackOutlined 
                    className={styles.metricIcon} 
                    style={{ color: parseFloat(returnRate) > 5 ? '#ef4444' : '#6b7280' }} 
                  />
                  <div className={styles.metricContent}>
                    <span className={styles.metricValue}>
                      {returns}
                      <Tag 
                        color={parseFloat(returnRate) > 5 ? 'red' : parseFloat(returnRate) > 2 ? 'orange' : 'green'}
                        className={styles.rateTag}
                      >
                        {returnRate}%
                      </Tag>
                    </span>
                    <span className={styles.metricLabel}>Returns</span>
                  </div>
                </div>
              </Tooltip>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  const customDataFetcher = isDriveFlowReportingEnabled()
    ? (id: string) => fetchProductDomainDashboardComponent(id, 'product_delivery')
    : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="product_delivery"
      showControls={!compact}
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ProductDeliveryComponent;
